<template>
<!-- eslint-disable max-len -->
<span>
  <transition enter-active-class="animated slideInDown" leave-active-class="animated fadeOut">
    <div class="report-container" id="report-container">
      <div :class="`report ${pdf ? 'pdf' : ''}`" ref="report" id="report">
        <div :class="`report-header ${pdf ? 'pdf' : ''}`">
          <!-- logo -->
          <img :src="resolveAssetPath(activeEnvironment.reportLogo || activeEnvironment.creditsLogo || activeEnvironment.titleLogo, 'ui/customer')" :class="`logo ${pdf ? 'pdf' : ''}`" ref="logo">
          <!-- title -->
          <h1 class="report-title no-wrap">{{activeEnvironment.reportTitle || game.reportTitle || 'PERSONAL REPORT'}}</h1>
        </div>

        <!-- name/date -->
        <div :style="{
          display: 'flex',
          'flex-flow': 'row nowrap',
          'justify-content': 'space-between',
          padding: '4px 50px',
          'font-size': '1.35em',
        }">
          <div class="no-wrap">
            {{username}}
          </div>
          <div class="no-wrap">
            {{date}}
          </div>
        </div>

        <!-- blue divider -->
        <div :style="{
          'background-color': '#05968f',
          height: '3px',
        }">
        </div>

        <!-- thank you blurb -->
        <div :style="{
          padding: '8px 50px',
          'font-size': '1em',
        }">
          Thank you for completing the human capabilities gamulation!
          Your personal report is based on your demonstrated decisions and preferences.
          What you've demonstrated expresses what comes most naturally to you at this time,
          which can give you clues into the types of roles, positions, and experiences that
          will often feel most aligned and empowering to you.
          There are no good, no bad, no better or no worse abilities.
          All humans can, and do, express every capability at different times and you can also develop all your capabilities.
          Below you can learn more about the capabilities that you most uniquely demonstrate in a work context.
        </div>

        <!-- chart -->
        <div :style="{
          display: 'flex',
          'flex-flow': 'row nowrap',
          'align-items': 'center',
          'justify-content': 'center',
          margin: `10px 0 ${pdf ? '35px' : '20px'}`,
          height: '222px',
          'background-color': 'white',
        }">
          <almas-radar
            :highlighted-index="selectedTab"
            :labels="sortedTopArchetypeNames"
            :icons="sortedTopArchetypeIcons"
            :values="sortedTopArchetypeValues"
          ></almas-radar>
        </div>

        <!-- tabs -->
        <div v-if="!pdf" class="archetype-tabs">
          <div v-for="(name, i) in sortedTopArchetypeNames"
            :key="i"
            class="archetype-tab"
            type="button"
            @click.prevent="() => onClickTab(i)"
          >
            {{name}}
          </div>
          <div :class="`selected-tab-highlight ${selectedTabClass}`">
          </div>
        </div>

        <!-- archetype header -->
        <div :class="`archetype-header ${selectedTabClass} no-wrap`">
          {{(sortedTopArchetypeNames && sortedTopArchetypeNames.length > selectedTab) ? sortedTopArchetypeNames[selectedTab] : 'THE CURIOUS'}}

          <!-- person with selected archetype icon -->
          <div :style="{
            position: 'absolute',
            top: '0',
            right: '0',
          }">
            <div :style="{
              position: 'absolute',
              top: '100px',
              right: '200px',
              'background-color': 'white',
              width: '3px',
              height: '350px',
              'z-index': '0',
            }">
            </div>
            <div class="person-background">
              <img class="person-icon"
                :src="`/static/reports/archetypes/${
                  selectedArchetypeIcon || 'Icon_Curious'
                }.png`"
              />
            </div>
          </div>
        </div>

        <!-- archetype summary -->
        <div :style="{
          margin: '10px 200px 10px 0',
          'font-size': '28px',
          'line-height': '130%',
          'min-height': '120px',
          padding: '0 100px',
        }">
          {{selectedArchetypeSummary}}
        </div>

        <!-- behaviors header -->
        <div :style="{
          margin: '12px 0 4px',
          padding: '3px 100px',
          'font-size': '19px',
          'background-color': '#05968f',
          color: 'white',
          position: 'relative',
          'z-index': '1',
        }">
          How might this show up at work? Behaviors that typically drive and fuel this archetype are:
        </div>

        <!-- behaviors -->
        <div :style="{
          margin: '6px 0 4px 790px',
          padding: '0 100px',
          'font-size': '10px',
        }">
          LEVEL
        </div>
        <div
          v-for="(b, i) in selectedArchetypeBehaviors"
          :key="i"
          :style="{
            display: 'flex',
            'flex-flow': 'row nowrap',
            'align-items': 'center',
            'justify-content': 'flex-start',
            padding: '0 100px',
            margin: '0 0 20px',
            height: '45px',
            'background-color': 'white',
          }"
        >
          <div class="behavior-bullet">
            <img class="behavior-bullet-icon"
              :src="`/static/reports/archetypes/${
                selectedArchetypeIcon || 'Icon_Curious'
              }.png`"
            />
          </div>
          <div :style="{
            width: '650px',
            margin: '0 45px 0 25px',
            'line-height': '125%',
          }">
            {{b.text}}
          </div>
          <star-box :stars="b.level"></star-box>
        </div>

        <!-- blue divider -->
        <div :style="{
          'background-color': '#05968f',
          height: '10px',
        }">
        </div>

        <!-- role card header -->
        <div :style="{
          margin: '8px 0',
          padding: '0 100px',
          'font-size': '19px',
          'font-weight': 'bold',
        }">
          As with all capabilities, you can put your strengths to work in any role, industry and profession.
          However, a few roles that you often find {{selectedArchetypeGroupName || 'the curious'}} in include:
        </div>

        <!-- role cards -->
        <div :style="{
          display: 'flex',
          'flex-flow': 'row nowrap',
          'align-items': 'center',
          'justify-content': 'flex-start',
          margin: '20px 0 0 100px',
          padding: '0 0 20px',
          height: '170px',
        }">
          <report-industry-role
            v-for="(r, i) in selectedArchetypeRoles"
            :key="i"
            :icon-class="r.icon"
            :category-color="selectedTabColor"
            :industry-role="r.name"
            :style="{
              margin: '0 18px 0 0',
            }"
          ></report-industry-role>
        </div>

        <!-- footer -->
        <div
          style="
            background-color: black;
            color: white;
            padding: 15px 25px 0;
            display: flex;
            flex-flow: row nowrap;
            justify-content: space-between;
            align-items: flex-start;
          "
        >
          <!-- footer message (e.g. displaying ©) -->
          <div v-html="activeEnvironment.reportFooter"></div>
          <!-- footer logo -->
          <img
            v-if="activeEnvironment.reportFooterLogo"
            :src="resolveAssetPath(activeEnvironment.reportFooterLogo, 'ui/customer')"
            :class="`footer-logo ${pdf ? 'pdf' : ''}`"
            ref="footerLogo"
          >
        </div>
      </div>
    </div>
  </transition>

  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <div v-if="showDownloading" class="download-message">Downloading!</div>
  </transition>

  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <button v-if="showNextButton"
      class="btn download-button"
      @click.prevent="onClickDownload"
    >Download</button>
  </transition>

  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <bottom-bar v-if="showNextButton" :next="showNarratorNotification ? onComplete : undefined" :tabindex="3" ref="bottomBar" :noBar="true">
      <span slot="button">
        <span v-html="(narrative && narrative.nextButtonText) ? narrative.nextButtonText : ((game && game.nextButtonDefaultText) ? game.nextButtonDefaultText : 'Next')"></span>
      </span>
    </bottom-bar>
  </transition>
</span>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import JsPDF from 'jspdf';
import domtoimage from 'dom-to-image-more';

import MetricsNavbar from '../ui/MetricsNavbar';
import Narrator from '../ui/Narrator';
import NarratorNotification from '../ui/NarratorNotification';
import BottomBar from '../ui/BottomBar';
import AlmasRadar from '../ui/AlmasRadar';
import ReportIndustryRole from '../ui/ReportIndustryRole';
import StarBox from '../ui/StarBox';

export default {
  // $emit 'input' on complete
  components: {
    MetricsNavbar,
    Narrator,
    NarratorNotification,
    BottomBar,
    AlmasRadar,
    ReportIndustryRole,
    StarBox,
  },
  data() {
    return {
      showNextButton: false,
      showDownloading: false,
      showNarrator: false,
      showNarratorNotification: false,
      selectedTab: 0,
      sortedTopArchetypeNames: ['THE CURIOUS', 'THE EMPATHIZER', 'THE DRIVER'],
      sortedTopArchetypeValues: [8, 7, 6],
      archetypeIcons: {
        'THE ANALYST': 'Icon_Analyst',
        'THE CURIOUS': 'Icon_Curious',
        'THE DECIDER': 'Icon_Decider',
        'THE PERCEPTIVE': 'Icon_Perceptive',
        'THE REGULATOR': 'Icon_Regulator',
        'THE STABILIZER': 'Icon_Stabilizer',
        'THE DRIVER': 'Icon_Driver',
        'THE PIONEER': 'Icon_Pioneer',
        'THE NAVIGATOR': 'Icon_Navigator',
        'THE CONNECTOR': 'Icon_Connector',
        'THE CHALLENGER': 'Icon_Challenger',
        'THE EMPATHIZER': 'Icon_Empathizer',
        'THE PRINCIPLED': 'Icon_Principled',
        'THE JUSTIFIED': 'Icon_Justified',
      },
      archetypeBehaviors: {
        'THE ANALYST': [
          { text: 'Being capable of identifying the key signals and data to pay attention to and track.', level: 0 },
          { text: 'Being able to develop competing hypotheses to determine and test what constitutes the primary challenge/problem.', level: 0 },
          { text: 'Being able to generate different, insightful and viable solutions to problems and determine probability of success.', level: 0 },
        ],
        'THE CURIOUS': [
          { text: 'Identifying new frameworks, ideas, models, strategies and work processes to experiment with to determine the best course of action.', level: 0 },
          { text: 'Engaging in challenging status quo thinking, encouraging others to do the same, including questioning guiding assumptions.', level: 0 },
          { text: 'Often providing new ideas that challenge the way we think about our work processes/outcomes.', level: 0 },
        ],
        'THE DECIDER': [
          { text: 'Being comfortable making hard decisions even if they don\'t have all of the requisite information to justify their decisions.', level: 0 },
          { text: 'Seeking to gain full consensus before making a difficult decision, but being able to move forward even when different opinions remain in their team.', level: 0 },
          { text: 'Being confident in going with their most reasoned judgement based on the data/facts available at the time.', level: 0 },
        ],
        'THE PERCEPTIVE': [
          { text: 'Accurately identifying their own emotions and how their expression impacts others in the workplace.', level: 0 },
          { text: 'Accurately identifying the expression of the emotions of others in their workplace.', level: 0 },
          { text: 'Understanding how to navigate and adjust their behaviors based on the emotional reactions of others.', level: 0 },
        ],
        'THE REGULATOR': [
          { text: 'Having the abilty to regulate their emotions with others based on the needs they express and challenges being faced.', level: 0 },
          { text: 'Being able to control their emotional reactions with others to help mitigate increasing stress levels when dealing with intense situations.', level: 0 },
          { text: 'Having developed strategies to regulate their emotions to more effectively deal with difficult problems at work.', level: 0 },
        ],
        'THE STABILIZER': [
          { text: 'Being able to sustain their calm demeanor when dealing with the aggressive behavior of others.', level: 0 },
          { text: 'Being able to maintain a high level of self-control when dealing with very difficult challenges.', level: 0 },
          { text: 'Maintaining a high level of focus when facing very difficult obstacles.', level: 0 },
        ],
        'THE DRIVER': [
          { text: 'Being able to create workplace objectives and goals even when the pathway forward is unclear. ', level: 0 },
          { text: 'Being motivated to obtain new skills to continuously improve their ability to master difficult performance challenges at work.', level: 0 },
          { text: 'Believing that consistently achieving the highest levels of performance is part of the way they view themselves.', level: 0 },
        ],
        'THE PIONEER': [
          { text: 'Being eager to examine new approaches, processes, technologies and ideas at work.', level: 0 },
          { text: 'Tending to prefer working with new unique challenges, versus challenges with known solutions.', level: 0 },
          { text: 'Seeking to work on new challenges, projects, routines and in emerging roles.', level: 0 },
        ],
        'THE NAVIGATOR': [
          { text: 'Being confident they can generate solutions to uncertain and complex challenges and be successful solving them.', level: 0 },
          { text: 'Believing their advancement at work is largely driven by their own actions.', level: 0 },
          { text: 'Believing their individual input into completing their work projects will substantially impact their performance outcomes.', level: 0 },
        ],
        'THE CONNECTOR': [
          { text: 'Seeking to make sure that all points of view are encouraged and processed before making final decisions.', level: 0 },
          { text: 'Continually challenging their own assumptions, while leveraging other individuals\' frames of reference, to inform their way of thinking.', level: 0 },
          { text: 'Promoting a high level of collaboration across all stakeholders in challenges, while valuing all points of view to arrive at the best solutions.', level: 0 },
        ],
        'THE CHALLENGER': [
          { text: 'Being constructive in their approach to questioning the obstacles that are placed by the organization\'s culture, policies and procedures that can inhibit effective performance.', level: 0 },
          { text: 'Sharing their ideas openly with all colleagues regardless of their position to challenge the way they think about the work that gets done.', level: 0 },
          { text: 'Being comfortable taking on a contrarian position even when their view is unpopular.', level: 0 },
        ],
        'THE EMPATHIZER': [
          { text: 'Recognizing the need to reach out to support the work of others when those individuals need assistance.', level: 0 },
          { text: 'Listening carefully to different points of view to understand their unique perspectives regarding a particular issue/challenge.', level: 0 },
          { text: 'Taking account of the needs and desires of others in supporting their development and performance.', level: 0 },
        ],
        'THE PRINCIPLED': [
          { text: 'Modeling high ethical standards in determining what is the best course of action that results in the least negative consequences for all individuals.', level: 0 },
          { text: 'Personally connecting with the ethical principles of conduct and guidelines that they and others must adhere to at work.', level: 0 },
          { text: 'Setting clear expectations for those who work for them in terms of what are the standards for what constitute high ethical standards.', level: 0 },
        ],
        'THE JUSTIFIED': [
          { text: 'Clarifying the processes that will be used to fairly judge all individuals in terms of adjudicating conflicting views/actions.', level: 0 },
          { text: 'Gathering input from others in order to come to a decision that is balanced and fair.', level: 0 },
          { text: 'Treating each individual regardless of any group they belong to with the highest standards of fairness.', level: 0 },
        ],
      },
      archetypeRoles: {
        'THE ANALYST': [
          { name: 'Investment Analysts', icon: 'chart-line-solid' },
          { name: 'Marketing Strategists', icon: 'chart-bar-solid' },
          { name: 'Economists', icon: 'dollar-sign-solid' },
          { name: 'Lawyers', icon: 'gavel-solid' },
          { name: 'Data Analysts', icon: 'chart-area-solid' },
        ],
        'THE CURIOUS': [
          { name: 'Researchers', icon: 'search-solid' },
          { name: 'Entrepreneurs', icon: 'lightbulb-solid' },
          { name: 'Marketing Professionals', icon: 'calculator-solid' },
          { name: 'Designers', icon: 'pencil-ruler-solid' },
          { name: 'Artists', icon: 'palette-solid' },
        ],
        'THE DECIDER': [
          { name: 'Management', icon: 'users-solid' },
          { name: 'Logistics Engineers', icon: 'list-solid' },
          { name: 'Police Officers', icon: 'bullhorn-solid' },
          { name: 'Principals', icon: 'school-solid' },
          { name: 'Surgeons', icon: 'user-md-solid' },
        ],
        'THE PERCEPTIVE': [
          { name: 'Therapists', icon: 'comments-solid' },
          { name: 'HR Representatives', icon: 'users-solid' },
          { name: 'Elementary School Teachers', icon: 'school-solid' },
          { name: 'Nurses', icon: 'medkit-solid' },
          { name: 'Rehabilitation Specialists', icon: 'heartbeat-solid' },
        ],
        'THE REGULATOR': [
          { name: 'Social Workers', icon: 'users-solid' },
          { name: 'Writers', icon: 'pencil-alt-solid' },
          { name: 'Career Coaches', icon: 'question-solid' },
          { name: 'Marketing Managers', icon: 'chart-pie-solid' },
          { name: 'Public Relations', icon: 'microphone-solid' },
        ],
        'THE STABILIZER': [
          { name: 'Emergency Doctors', icon: 'hospital-solid' },
          { name: 'Paramedics', icon: 'ambulance-solid' },
          { name: 'Emergency Workers', icon: 'fire-solid' },
          { name: 'Speech-language Pathologists', icon: 'comment-solid' },
          { name: 'Veterinarians', icon: 'dog-solid' },
        ],
        'THE DRIVER': [
          { name: 'Sales And Advertising', icon: 'dollar-sign-solid' },
          { name: 'Real Estate Agents', icon: 'home-solid' },
          { name: 'Politicians', icon: 'handshake-solid' },
          { name: 'Investment Bankers', icon: 'university-solid' },
          { name: 'Social Media Marketers', icon: 'share-square-solid' },
        ],
        'THE PIONEER': [
          { name: 'Architects', icon: 'building-solid' },
          { name: 'Creative Project Coordinators', icon: 'lightbulb-solid' },
          { name: 'IT Professionals', icon: 'laptop-solid' },
          { name: 'Researchers', icon: 'search-solid' },
          { name: 'Non-Profit Leaders', icon: 'globe-americas-solid' },
        ],
        'THE NAVIGATOR': [
          { name: 'Entrepreneurs', icon: 'briefcase-solid' },
          { name: 'Freelancers', icon: 'code-solid' },
          { name: 'Information Technology', icon: 'server-solid' },
          { name: 'Consultants', icon: 'comment-solid' },
          { name: 'R&D Professionals', icon: 'search-solid' },
        ],
        'THE CONNECTOR': [
          { name: 'DE&I Leaders', icon: 'users-solid' },
          { name: 'Service Industry Leaders', icon: 'shopping-cart-solid' },
          { name: 'Entertainers', icon: 'tv-solid' },
          { name: 'Politicians', icon: 'handshake-solid' },
          { name: 'Recruiters', icon: 'envelope-solid' },
        ],
        'THE CHALLENGER': [
          { name: 'Legislators', icon: 'university-solid' },
          { name: 'Environmental Engineers', icon: 'leaf-solid' },
          { name: 'Investigative Journalists', icon: 'camera-solid' },
          { name: 'Lobbyists', icon: 'comment-solid' },
          { name: 'Lawyers', icon: 'gavel-solid' },
        ],
        'THE EMPATHIZER': [
          { name: 'Emergency Medical Technicians', icon: 'ambulance-solid' },
          { name: 'Fundraising Specialists', icon: 'dollar-sign-solid' },
          { name: 'Humanitarian Aid Workers', icon: 'globe-americas-solid' },
          { name: 'Career Coaches', icon: 'question-solid' },
          { name: 'Team Leaders', icon: 'users-solid' },
        ],
        'THE PRINCIPLED': [
          { name: 'Police Officers', icon: 'bullhorn-solid' },
          { name: 'Mediators', icon: 'comments-solid' },
          { name: 'Corporate Responsibility Managers', icon: 'eye-solid' },
          { name: 'Compliance Officers', icon: 'list-solid' },
          { name: 'Child Welfare Specialists', icon: 'child-solid' },
        ],
        'THE JUSTIFIED': [
          { name: 'Judges', icon: 'gavel-solid' },
          { name: 'Forensic Psychologists', icon: 'brain-solid' },
          { name: 'HSI Agents', icon: 'search-solid' },
          { name: 'Non-Profit Executives', icon: 'globe-americas-solid' },
          { name: 'Human Rights Professionals', icon: 'user-solid' },
        ],
      },
      pdf: false
    };
  },
  computed: {
    ...mapGetters([
      'activeEnvironment',
      'activeClass',
      'characters',
      'tooltip',
      'narrative',
      'lastNarrative',
      'nextNarrative',
      'narrativeNavbarText',
      'narrativeText',
      'group',
      'me',
      'metrics',
      'game',
      'dateActiveGameCompleted',
      'challenge'
    ]),
    username() {
      const {
        firstName = '',
        lastName = '',
        email = ''
      } = this.me || {};
      return firstName || lastName ? `${firstName} ${lastName}` : email;
    },
    date() {
      return (this.game.carryOverMetricsFromActive && this.dateActiveGameCompleted != null)
        ? new Date(this.dateActiveGameCompleted).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
        : ((this.challenge && this.challenge.completed)
          ? new Date(this.challenge.completed).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
          : ((this.group && this.group.updated)
            ? new Date(this.group.updated).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
            : new Date(Date.now()).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })));
    },
    reportFileName() {
      return (this.activeClass != null && this.activeClass.studentReportFileName != null)
        ? this.activeClass.studentReportFileName
        : (this.activeEnvironment.reportTitle || this.game.reportTitle || 'Personal Report');
    },
    selectedTabClass() {
      if (this.selectedTab === 0) {
        return 'first';
      }

      if (this.selectedTab === 1) {
        return 'second';
      }

      if (this.selectedTab === 2) {
        return 'third';
      }
    },
    selectedTabColor() {
      if (this.selectedTab === 0) {
        return 'red';
      }

      if (this.selectedTab === 1) {
        return 'blue';
      }

      if (this.selectedTab === 2) {
        return 'yellow';
      }
    },
    selectedArchetype() {
      return (this.sortedTopArchetypeNames
        && this.sortedTopArchetypeNames.length > this.selectedTab)
        ? this.sortedTopArchetypeNames[this.selectedTab]
        : 'THE CURIOUS';
    },
    selectedArchetypeSummary() {
      if (this.selectedArchetype === 'THE ANALYST') {
        return 'You discern patterns within information, extracting meaning from the whole picture.';
      }
      if (this.selectedArchetype === 'THE CURIOUS') {
        return 'You often behave in ways that help to uncover new ideas, perspectives, experimentation, exploration, and investigation.';
      }
      if (this.selectedArchetype === 'THE DECIDER') {
        return 'You feel at ease addressing difficult, complex, and challenging situations to make decisions more effectively. ';
      }
      if (this.selectedArchetype === 'THE PERCEPTIVE') {
        return 'You can identify how the expression of your emotions impacts others and how another person’s emotions impact you.';
      }
      if (this.selectedArchetype === 'THE REGULATOR') {
        return 'You can recognize and address how to best respond to the emotional reactions and needs of others.';
      }
      if (this.selectedArchetype === 'THE STABILIZER') {
        return 'You can remain balanced in your demeanor and emotions when addressing difficult situations.';
      }
      if (this.selectedArchetype === 'THE DRIVER') {
        return 'You have the ability to maintain a high level of internal motivation to achieve the goals that have been set by yourself or your organization.';
      }
      if (this.selectedArchetype === 'THE PIONEER') {
        return 'You maintain a high level of receptivity to new ideas, experiences, and a diverse range of people.';
      }
      if (this.selectedArchetype === 'THE NAVIGATOR') {
        return 'You see yourself as having more control over the performance outcome of events than seeing external events/forces as the driver of outcomes.';
      }
      if (this.selectedArchetype === 'THE CONNECTOR') {
        return 'You actively foster the importance of engaging with a diverse range of individuals to get the best work done.';
      }
      if (this.selectedArchetype === 'THE CHALLENGER') {
        return 'You excel at, and model for others, the ability to raise different points of view, encouraging healthy conflict and discussion.';
      }
      if (this.selectedArchetype === 'THE EMPATHIZER') {
        return 'You recognize the needs and abilities of your colleagues and adjust your approach to optimizing team performance.';
      }
      if (this.selectedArchetype === 'THE PRINCIPLED') {
        return 'You determine the ethical consequences of important decisions and actions to consider what represents the fairest options for all stakeholders';
      }
      if (this.selectedArchetype === 'THE JUSTIFIED') {
        return 'You judge all sides of an argument, decisions, or actions to come to a conclusion on the best course of action that is equitable and fair.';
      }
      return 'You often behave in ways that help to uncover new ideas, perspectives, experimentation, exploration, and investigation.';
    },
    selectedArchetypeIcon() {
      return this.archetypeIcons[this.selectedArchetype];
    },
    selectedArchetypeGroupName() {
      if (this.selectedArchetype === 'THE ANALYST') {
        return 'analysts';
      }
      if (this.selectedArchetype === 'THE CURIOUS') {
        return 'the curious';
      }
      if (this.selectedArchetype === 'THE DECIDER') {
        return 'deciders';
      }
      if (this.selectedArchetype === 'THE PERCEPTIVE') {
        return 'the perceptive';
      }
      if (this.selectedArchetype === 'THE REGULATOR') {
        return 'regulators';
      }
      if (this.selectedArchetype === 'THE STABILIZER') {
        return 'stabilizers';
      }
      if (this.selectedArchetype === 'THE DRIVER') {
        return 'drivers';
      }
      if (this.selectedArchetype === 'THE PIONEER') {
        return 'pioneers';
      }
      if (this.selectedArchetype === 'THE NAVIGATOR') {
        return 'navigators';
      }
      if (this.selectedArchetype === 'THE CONNECTOR') {
        return 'connectors';
      }
      if (this.selectedArchetype === 'THE CHALLENGER') {
        return 'challengers';
      }
      if (this.selectedArchetype === 'THE EMPATHIZER') {
        return 'empathizers';
      }
      if (this.selectedArchetype === 'THE PRINCIPLED') {
        return 'the principled';
      }
      if (this.selectedArchetype === 'THE JUSTIFIED') {
        return 'the justified';
      }
      return 'the curious';
    },
    selectedArchetypeBehaviors() {
      return this.archetypeBehaviors[this.selectedArchetype];
    },
    selectedArchetypeRoles() {
      return this.archetypeRoles[this.selectedArchetype];
    },
    sortedTopArchetypeIcons() {
      return this.sortedTopArchetypeNames.map(n => this.archetypeIcons[n]);
    },
  },
  methods: {
    ...mapActions([
      'getMyGroup'
    ]),
    async show() {
      this.showNarrator = true;
      await new Promise(resolve => setTimeout(resolve, 1250));
      this.showNarratorNotification = true;
      await new Promise(resolve => setTimeout(resolve, 1250));
      await Vue.nextTick();
    },
    async reset() {
      await Vue.nextTick();
      await this.show();
    },
    navigate() {
      this.$refs.bottomBar.navigate();
    },
    async onComplete() {
      if (this.tooltip && this.tooltip.preventStateProgressionWhileOpen) {
        window.dispatchEvent(new Event('click'));
        return;
      }
      this.$emit('input');
    },
    async loadPageData() {
      try {
        const { _id: groupId } = this.group;
        await this.getMyGroup({ groupId });
      } catch (err) {
        if (await this.$router.handleGameError(err)) return;
        this.$router.showGameErrorCritical();
      }
    },
    setStats() {
      // console.log(this.group);
      // TODO: the following order-dependent coupling is not ideal, but it currently seems
      //       to be the best way to do this given the way the data is currently organized.
      //       An ID-based coupling or a name-based coupling seems similarly dicey,
      //       and they'd just be costlier to compute.

      const archetypeScores = [];
      for (let i = 0; i < this.group.metrics.length; i += 4) {
        archetypeScores.push({ index: i, value: this.group.metrics[i].value });
      }
      archetypeScores.sort((a, b) => b.value - a.value);

      // console.log(archetypeScores);

      if (archetypeScores.length > 2) {
        this.sortedTopArchetypeNames.length = 0;
        this.sortedTopArchetypeNames.push(this.archetypeIndexMap(archetypeScores[0].index));
        this.sortedTopArchetypeNames.push(this.archetypeIndexMap(archetypeScores[1].index));
        this.sortedTopArchetypeNames.push(this.archetypeIndexMap(archetypeScores[2].index));
        this.sortedTopArchetypeValues.length = 0;
        this.sortedTopArchetypeValues.push(archetypeScores[0].value);
        this.sortedTopArchetypeValues.push(archetypeScores[1].value);
        this.sortedTopArchetypeValues.push(archetypeScores[2].value);
        this.sortedTopArchetypeNames.forEach((n, i) => {
          const behaviors = this.archetypeBehaviors[n];
          behaviors[0].level = this.group.metrics[archetypeScores[i].index + 1].value;
          behaviors[1].level = this.group.metrics[archetypeScores[i].index + 2].value;
          behaviors[2].level = this.group.metrics[archetypeScores[i].index + 3].value;
        });
      }
    },
    archetypeIndexMap(metricIndex) {
      switch (metricIndex) {
        case 52: return 'THE STABILIZER';
        case 48: return 'THE REGULATOR';
        case 44: return 'THE PIONEER';
        case 40: return 'THE JUSTIFIED';
        case 36: return 'THE NAVIGATOR';
        case 32: return 'THE CONNECTOR';
        case 28: return 'THE PRINCIPLED';
        case 24: return 'THE DRIVER';
        case 20: return 'THE DECIDER';
        case 16: return 'THE CURIOUS';
        case 12: return 'THE EMPATHIZER';
        case 8: return 'THE CHALLENGER';
        case 4: return 'THE PERCEPTIVE';
        case 0: return 'THE ANALYST';
        default: return '';
      }
    },
    onBeforePrint(mql) {
      if (mql instanceof Event) {
        this.pdf = true;
        setTimeout(() => {
          this.pdf = false;
        }, 250);
      } else if (mql.matches) {
        this.pdf = true;
        setTimeout(() => {
          this.pdf = false;
        }, 250);
      }
    },
    onClickTab(index) {
      this.selectedTab = index;
    },
    async onClickDownload() {
      if (this.showDownloading) return;
      this.showDownloading = true;
      // PDF download works in all browsers except pre-chromium Edge
      // (fails at domtoimage.toPng, and no replacement seems viable, see above)
      // fix for Edge (for now): make the page printable, and open the print dialog automatically
      // (in response to domtoimage.toPng or any other part of the autodownload throwing any error)
      // The user can print to PDF or actually print. Bonus is you can print in other browsers too.
      // Edge requires the user to manually set the Scale in the print dialog to 50% for it to fit.
      // Note that this is generally no longer a problem with the new chromium-based Edge.
      this.pdf = true;
      try {
        this.selectedTab = 0;
        await Vue.nextTick();
        let dataUrl = await domtoimage.toPng(this.$refs.report, { cacheBust: true });
        const doc = new JsPDF('p', 'pt', 'a4');
        doc.addImage(dataUrl, 'PNG', 28, 50, 540, 690);
        this.selectedTab = 1;
        await Vue.nextTick();
        doc.addPage();
        dataUrl = await domtoimage.toPng(this.$refs.report, { cacheBust: true });
        doc.addImage(dataUrl, 'PNG', 28, 50, 540, 690);
        this.selectedTab = 2;
        await Vue.nextTick();
        doc.addPage();
        dataUrl = await domtoimage.toPng(this.$refs.report, { cacheBust: true });
        doc.addImage(dataUrl, 'PNG', 28, 50, 540, 690);
        this.selectedTab = 0;

        // TODO: if no autodownload, no soft-lock (explained below), so soft-lock fix commented out
        doc.save(`${this.reportFileName}.pdf`);
        // Before we download the PDF, we have to consider the possibility that the
        // user's settings are set to view the PDF in the browser first when downloading a PDF
        // (instead of actually downloading it). This can be true in many modern browsers.
        // This is a problem because if it opens in the same page, it destroys the Vue app state.
        // This ends up soft-locking the player such that they can't get past the report page.
        // This problem came to my attention when a Firefox update changed the PDF download behavior
        // to default to viewing it in browser (on the same page) when you initiate a download.
        // To fix this while minimally impacting anyone whose browser actually just downloads stuff
        // when a download is initiated, instead first open a new tab, then artificially initiate
        // the download with a bogus DOM hyperlink element that targets the bloburl output by JsPDF,
        // which either downloads the file or loads the blob viewer. If it doesn't load the
        // blob viewer, we autoclose the tab, leaving you back on the report page. If it does load
        // the blob viewer, the user is left in the blob viewer, where they can either:
        // - close it, returning to the original report tab without downloading, where they can
        //   hit print or refresh to save off the report another way, or continue on with the game.
        // - download/print it (which presents a system dialog, and does carry over the expected
        //   default name) (and then close it to return back to the game).
        // eslint-disable-next-line max-len
        // const win = window.open(`${window.location.origin}/downloadplayerreport/${encodeURIComponent(this.reportFileName)}/${encodeURIComponent(doc.output('bloburl', { filename: `${this.reportFileName}.pdf` }))}`, '_blank');
        // if (win == null) {
        //   doc.save(`${this.reportFileName}.pdf`);
        // }

        this.pdf = false;
        this.showDownloading = false;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to download report PDF!', error);
        try {
          this.pdf = true;
          this.selectedTab = 0;
          await Vue.nextTick();
          window.print();
          this.pdf = false;
          this.showDownloading = false;
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('Failed to open print dialog!', e);
          this.pdf = false;
          this.showDownloading = false;
          this.selectedTab = 0;
        }
      }
    },
  },
  watch: {
    async narrative(newValue) {
      if (newValue) await this.reset();
    },
  },
  async mounted() {
    this.setStats();

    // polyfill for safari but works for others as well
    const mediaQueryList = (window.matchMedia != null) ? window.matchMedia('print') : null;
    if (mediaQueryList != null && mediaQueryList.addListener != null) {
      mediaQueryList.removeListener(this.onBeforePrint);
      mediaQueryList.addListener(this.onBeforePrint);
    } else {
      // what you'd normally use, if you weren't needing to support Safari:
      window.removeEventListener('beforeprint', this.onBeforePrint);
      window.addEventListener('beforeprint', this.onBeforePrint);
    }

    await this.show();

    await new Promise((resolve, reject) => {
      if (this.$refs.logo.complete) { // wait for logo to load
        resolve();
      } else {
        this.$refs.logo.addEventListener('load', resolve);
        this.$refs.logo.addEventListener('error', reject);
      }
    });

    if (this.activeEnvironment.reportFooterLogo && this.$refs.footerLogo) {
      await new Promise((resolve, reject) => {
        if (this.$refs.footerLogo.complete) { // wait for footer logo to load
          resolve();
        } else {
          this.$refs.footerLogo.addEventListener('load', resolve);
          this.$refs.footerLogo.addEventListener('error', reject);
        }
      });
    }

    // uncomment to enable autodownload (but re-add soft-lock fix above if you do)
    // await this.onClickDownload();

    this.showNextButton = true;
  },
};
</script>

<style lang="scss" scoped>
@media print {
  body, body * {
    visibility: hidden !important;
    overflow: visible !important;
  }
  #report-container, #report-container * {
    visibility: visible !important;
    overflow: visible !important;
  }
  #report-container {
    left: 8.75% !important;
    top: 0 !important;
    width: 82.5% !important;
    overflow: visible !important;
    z-index: 10 !important;

    // this makes background graphics show up without needing to check the box
    color-adjust: exact !important;
    -webkit-print-color-adjust: exact !important;
  }
}

.no-wrap {
  white-space: nowrap;
}

.download-button {
  position: absolute;
  bottom: 210px;
  right: 0;
  width: 350px;
  font-size: 28px;
  line-height: 55px;
  margin-right: 45px;
  z-index: 100 !important;
  transition: opacity 0.5s ease-in-out;
}

hr {
  border-top: 1px solid #e9ecef;
}
.report {
  background-color: lightgrey;
  color: black;
}
.report.pdf {
  border: solid;
  border-width: 3.5px;
  border-color: var(--primaryColor);
}
.report-container {
  position: absolute;
  width: 1150px;
  height: 850px;
  top: 30px;
  left: 30px;
  z-index: 1;
  overflow: auto;
}

.report-header {
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  height: 60px;
  padding: 0 50px;
  background-color: black;
  color: lightgrey;
}
.logo {
  object-fit: contain;
  height: 90%;
  max-width: 40%;
  z-index: 10;
}
.footer-logo {
  object-fit: contain;
  max-height: 30px;
  max-width: 30%;
  margin-bottom: 15px;
  z-index: 10;
}
.report-title {
  font-size: 1.96em;
  white-space: pre-wrap;
  text-align: right;
  margin: 0;
  max-width: 60%;
  max-height: 100%;
  line-height: 100%;
}

.archetype-tabs {
  width: 100%;
  height: 80px;
  background-image: url('/static/reports/tabbg.png');
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
  margin: 35px 0 10px 0;
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-evenly;
  align-items: center;
  padding: 0 78px;
}

.archetype-tab {
  width: calc(33% - 156px);
  font-size: 21px;
  color: white;
  text-align: center;
  white-space: nowrap;
  -webkit-appearance: none;
}

.selected-tab-highlight {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  pointer-events: none;
}

.selected-tab-highlight.first {
  background-image: url('/static/reports/tabselectedred.png');
}

.selected-tab-highlight.second {
  background-image: url('/static/reports/tabselectedblue.png');
}

.selected-tab-highlight.third {
  background-image: url('/static/reports/tabselectedyellow.png');
}

.archetype-header {
  width: 100%;
  height: 80px;
  padding: 0 100px;
  font-size: 30px;
  color: white;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;
  position: relative;
}

.archetype-header.first {
  background-image: url('/static/reports/headerbgred.png');
}

.archetype-header.second {
  background-image: url('/static/reports/headerbgblue.png');
}

.archetype-header.third {
  background-image: url('/static/reports/headerbgyellow.png');
}

.behavior-bullet {
  width: 60px;
  height: 60px;
  background-image: url('/static/reports/bulletbg.png');
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
}

.behavior-bullet-icon {
  width: 59%;
  height: 59%;
  object-fit: contain;
  margin: 0 0 8px 4px;
}

.person-background {
  position: absolute;
  top: 0;
  right: 100px;
  width: 200px;
  height: 200px;
  background-image: url('/static/reports/person.png');
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 67px 0 0 0;
}

.person-icon {
  width: 50%;
  height: 50%;
  object-fit: contain;
}

.slideInDown {
  animation-duration: 1s;
  animation-timing-function: cubic-bezier(0.19, 1, 0.22, 1); // out expo
}
.fadeOutUp {
  animation-duration: 1s;
  animation-timing-function: cubic-bezier(0.55, 0.055, 0.675, 0.19); // in cubic
}
.narrator-notification {
  bottom: 205px;
}

.download-message {
  position: absolute;
  top: 50%;
  left: 25%;
  z-index: 1000;
  border-radius: 15px;
  border: 3px solid black;
  background-color: var(--primaryColor);
  color: white;
  font-size: 30pt;
  padding: 20px;
  font-weight: bold;
}
</style>



// WEBPACK FOOTER //
// src/components/game/narrative/NarrativeAlmasReport.vue